/**
 * Receipt photo compression, before the upload rather than after (P1-TR4, NFR-2).
 *
 * A modern phone camera produces a 3-5 MB JPEG. On a rural 3G connection that
 * is a minute of uploading during which the partner is standing in a feed shop
 * holding their phone up, and it is the step at which people give up and stop
 * recording receipts — which makes BR-06 unenforceable in practice.
 *
 * So the browser resizes and re-encodes first, targeting 300 KB. A receipt only
 * has to be legible: 1600px on the long edge is more than enough to read a
 * handwritten total, and it is roughly a twentieth of the bytes.
 *
 * The server does not trust any of this. It sniffs magic bytes and enforces its
 * own 5 MB cap (SEC-5); this is a courtesy to the partner's data allowance, not
 * a security control.
 */

export const TARGET_BYTES = 300 * 1024;
export const MAX_EDGE_PX = 1600;

/** Quality steps tried in order. Below 0.4 a receipt starts to be hard to read. */
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5, 0.4] as const;

export interface CompressOptions {
  targetBytes?: number;
  maxEdge?: number;
}

export interface CompressResult {
  file: File;
  originalBytes: number;
  bytes: number;
  width: number;
  height: number;
}

/** The dimensions that fit inside `maxEdge` without changing the aspect ratio. */
export function scaledDimensions(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE_PX,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };

  const ratio = maxEdge / longest;
  // Round rather than floor: a 1600x1199.6 image should be 1200 tall, and a
  // zero would make the canvas throw.
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
}

/**
 * Compresses an image file, returning the original untouched if it is already
 * small enough.
 *
 * Never throws for image reasons. A partner in a shop with a receipt in one
 * hand should not lose their entry because a canvas call failed on an unusual
 * device — the original file is uploaded instead, and the server's 5 MB cap
 * decides.
 */
export async function compressImage(file: File, options: CompressOptions = {}): Promise<CompressResult> {
  const targetBytes = options.targetBytes ?? TARGET_BYTES;
  const maxEdge = options.maxEdge ?? MAX_EDGE_PX;

  const unchanged = (width = 0, height = 0): CompressResult => ({
    file,
    originalBytes: file.size,
    bytes: file.size,
    width,
    height,
  });

  if (!file.type.startsWith('image/')) return unchanged();

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = scaledDimensions(bitmap.width, bitmap.height, maxEdge);

    // Already small and already modest in size: re-encoding would cost quality
    // for nothing.
    if (file.size <= targetBytes && width === bitmap.width && height === bitmap.height) {
      return unchanged(bitmap.width, bitmap.height);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) return unchanged(bitmap.width, bitmap.height);
    context.drawImage(bitmap as CanvasImageSource, 0, 0, width, height);

    // Step the quality down until it fits. The last step is used even if it
    // still overshoots: a large photo is better than no receipt, and it is
    // always smaller than what the camera produced.
    let best: Blob | null = null;
    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, quality);
      if (!blob) continue;
      best = blob;
      if (blob.size <= targetBytes) break;
    }

    if (!best || best.size >= file.size) return unchanged(bitmap.width, bitmap.height);

    return {
      file: new File([best], renameToJpeg(file.name), { type: 'image/jpeg', lastModified: Date.now() }),
      originalBytes: file.size,
      bytes: best.size,
      width,
      height,
    };
  } catch {
    return unchanged();
  }
}

/** `.heic` and `.png` become `.jpg`, because that is what was encoded. */
const renameToJpeg = (name: string): string => `${name.replace(/\.[^.]+$/, '')}.jpg`;

async function loadBitmap(file: File): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file);

  // Older Android WebViews have no createImageBitmap; an <img> and an object
  // URL work everywhere and cost one more frame.
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image'));
    };
    image.src = url;
  });
}

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
